/** @format */

import { ActivatedRouteSnapshot } from '@angular/router';
import { catchError, map } from 'rxjs/operators';

import { DCSG_CONSTANTS } from '@shared/data-access/constants';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { chain } from '@core/chain';
import { of } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
import { HttpService } from '@shared/data-access/services';
import { ProductService } from '@shared/data-access/product';
import { Store } from '@ngrx/store';
import { setCommonPageType } from '@state/data-access';
import { ProductSelectionService } from '@pdp/data-access/services';

@Injectable({
	providedIn: 'root'
})
export class ProductResolveGuard {
	constructor(
		private productService: ProductService,
		private productSelectionService: ProductSelectionService,
		private httpService: HttpService,
		private store: Store,
		@Inject(PLATFORM_ID) private platformId
	) {
		this.store.dispatch(setCommonPageType({ pageType: 'pdp' }));
	}

	public resolve(route: ActivatedRouteSnapshot) {
		if (
			typeof route.params['seoToken'] === 'string' &&
			route.params['seoToken'].trim() !== '' &&
			typeof route.params['partNumber'] === 'string' &&
			route.params['partNumber'].trim() !== ''
		) {
			return this.productService
				.getProductByUrl$(
					route.params['seoToken'],
					route.params['partNumber'],
					chain.chainMarketingIdentifierAbbr,
					route.queryParams['Clearance'] || route.queryParams['clearance'],
					route.queryParams['antiClearance'],
					route.queryParams['categoryId'],
					undefined,
					1
				)
				.pipe(
					map((product) => {
						if (typeof product !== 'undefined') {
							if (
								!isPlatformBrowser(this.platformId) &&
								product.redirect &&
								product.redirect.redirectUrl
							) {
								// 301 Redirect
								this.httpService.redirect(product.redirect.redirectUrl);
							} else if (product.type === 'style') {
								if (
									typeof product.productsData[0]?.skus === 'undefined' ||
									product.productsData[0]?.skus?.length === 0
								) {
									// Set the 410 status code
									this.httpService.setStatusCode(410);
								}
								this.productSelectionService.initializeAppDependentDataToState(
									product.productsData
								);
							} else {
								if (
									typeof product.productsData === 'undefined' ||
									typeof product.bundleData === 'undefined'
								) {
									// Set the 410 status code
									this.httpService.setStatusCode(410);
								}
								this.productSelectionService.initializeBundleDependentDataToState(
									product
								);
							}

							return product;
						} else {
							return null;
						}
					}),
					catchError((error: object | string) => {
						// Log the error
						if (isPlatformBrowser(this.platformId)) {
							console.error(
								`${
									DCSG_CONSTANTS.errorHandling.messagePrefixes.global.http
								}Unable to get product by route params -- eCode: '${
									route.params['partNumber']
								}', clearance: '${
									route.queryParams['Clearance'] || route.queryParams['clearance']
								}', categoryId: '${
									route.queryParams['categoryId']
								}', productParamType: undefined. ${this.getErrorToPrint(error)}`,
								error
							);
						}
						// Return an undefined Observable
						return of(null);
					})
				);
		} else {
			// Return an undefined Observable
			return of(null);
		}
	}

	/**
	 * @description
	 * Accepts an error and stringifies the important parts
	 */
	private getErrorToPrint(error: object | string): string {
		try {
			return JSON.stringify(error, ['status', 'message', 'arguments', 'type', 'name']);
		} catch (error) {
			// Swallow the JSON stringify error
			return '';
		}
	}
}
